import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Filter, X, Camera } from 'lucide-react';
import { StatusBadge } from '../components/ui/StatusBadge';
import { SkeletonList } from '../components/ui/Skeleton';
import { SearchBar } from '../components/ui/SearchBar';
import { DamageReport, Project, Photo } from '../types';
import { getProject } from '../services/projectService';
import { getDamageReportsByProject } from '../services/damageReportService';
import { getPhotosByProject } from '../services/photoService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

const DamageReportListPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const [project, setProject] = useState<Project | null>(null);
  const [reports, setReports] = useState<DamageReport[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<DamageReport['status'] | 'all'>('all');
  const [photoLoadError, setPhotoLoadError] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);

        const [fetchedProject, fetchedReports, projectPhotos] = await Promise.all([
          getProject(projectId),
          getDamageReportsByProject(projectId),
          getPhotosByProject(projectId),
        ]);
        
        if (!fetchedProject) {
          setError('Projektet kunde inte hittas. Kontrollera att projektets ID är korrekt.');
          setLoading(false);
          return;
        }
        setProject(fetchedProject);
        setReports(fetchedReports);
        setPhotos(projectPhotos);

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Kunde inte hämta data. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Get photos for a specific report
  const getReportPhotos = (report: DamageReport) => {
    if (!report.photoIds || report.photoIds.length === 0) return [];
    return photos.filter(photo => report.photoIds.includes(photo.id));
  };

  // Handle image load error
  const handleImageError = (photoId: string) => {
    setPhotoLoadError(prev => ({
      ...prev,
      [photoId]: true
    }));
  };

  const filteredReports = reports.filter(report => {
    // Filter by status if not 'all'
    if (statusFilter !== 'all' && report.status !== statusFilter) {
      return false;
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        report.description.toLowerCase().includes(searchLower) ||
        report.reportedBy.toLowerCase().includes(searchLower) ||
        (report.resolution && report.resolution.toLowerCase().includes(searchLower))
      );
    }

    return true;
  });

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Okänt datum';
    
    try {
      const date = timestamp.toDate();
      return date.toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Ogiltigt datum';
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-3 space-y-3">
        <SkeletonList count={3} />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="space-y-2">
        <div className="sticky top-header z-20 bg-[var(--bg-surface)] backdrop-blur-sm border-b border-[var(--border-default)] px-3 py-2 flex items-center justify-between">
          <Link to={projectId ? `/projects/${projectId}` : '/'} className="flex items-center text-[var(--text-secondary)] hover:text-accent-600 text-sm py-1.5 px-2 -ml-1 min-h-[36px] min-w-[36px] rounded hover:bg-[var(--bg-surface-alt)] transition-colors">
            <ArrowLeft size={18} className="mr-1" />
            <span className="font-medium">Tillbaka</span>
          </Link>
          <h1 className="text-sm font-semibold text-center flex-1 mx-2">Skaderapporter</h1>
        </div>
        
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-3 py-2 rounded text-sm">
          {error || 'Projektet kunde inte hittas.'}
          {!isOnline && (
            <p className="mt-2 text-sm">
              Du är offline. Kontrollera din internetanslutning och försök igen.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative pb-28 space-y-2">
      {/* Top Navigation Bar */}
      <div className="sticky top-header z-20 bg-[var(--bg-surface)] backdrop-blur-sm border-b border-[var(--border-default)] px-3 py-2 flex items-center justify-between">
        <Link to={`/projects/${projectId}`} className="flex items-center text-[var(--text-secondary)] hover:text-accent-600 text-sm py-1.5 px-2 -ml-1 min-h-[36px] min-w-[36px] rounded hover:bg-[var(--bg-surface-alt)] transition-colors">
          <ArrowLeft size={18} className="mr-1" />
          <span className="font-medium">Tillbaka</span>
        </Link>
        
        <h1 className="text-sm font-semibold text-center flex-1 mx-2">
          Skaderapporter - {project.address.street}
        </h1>
        
        <Link
          to={`/projects/${projectId}/damage-reports/new`}
          className="flex items-center text-white bg-accent-600 hover:bg-accent-700 py-2 px-3 rounded-xl text-sm transition-colors"
        >
          <Plus size={16} className="mr-1" />
          <span className="font-medium">Ny skaderapport</span>
        </Link>
      </div>
      
      <div className="bg-[var(--bg-surface)] rounded-ios-card border border-[var(--border-default)]/80 p-3 lg:p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Sök skaderapporter..."
            className="w-full sm:w-64"
          />
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter size={14} className="text-accent-500/70" />
            <select
              className="border border-[var(--border-default)] rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent-500 text-sm w-full sm:w-auto"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DamageReport['status'] | 'all')}
            >
              <option value="all">Alla statusar</option>
              <option value="open">Öppna</option>
              <option value="in-progress">Pågående</option>
              <option value="resolved">Lösta</option>
            </select>
          </div>
        </div>
        
        {filteredReports.length === 0 ? (
          <div className="bg-[var(--bg-surface)] rounded-ios-card border border-[var(--border-default)]/80 p-3 text-center">
            <X size={20} className="text-[var(--text-tertiary)] mx-auto mb-2" />
            <p className="text-sm font-semibold text-[var(--text-primary)]">Inga skaderapporter att visa</p>
            <p className="mt-2 text-xs text-[var(--text-secondary)]">Klicka på "Ny skaderapport" för att komma igång</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredReports.map((report) => {
              const reportPhotos = getReportPhotos(report);
              const hasValidPhoto = reportPhotos.length > 0 && !photoLoadError[reportPhotos[0].id];
              
              return (
                <Link
                  key={report.id}
                  to={`/projects/${projectId}/damage-reports/${report.id}`}
                  className="block border border-[var(--border-default)]/80 rounded-ios-card p-3 press-row bg-[var(--bg-surface)]"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-2">
                      {hasValidPhoto ? (
                        <div className="w-14 h-14 rounded overflow-hidden flex-shrink-0 bg-[var(--bg-surface-alt)] border border-[var(--border-default)]">
                          <img 
                            src={reportPhotos[0].url} 
                            loading="lazy"
                            alt="Skadebild" 
                            className="w-full h-full object-cover"
                            onError={() => handleImageError(reportPhotos[0].id)}
                          />
                        </div>
                      ) : (
                        <div className="w-14 h-14 rounded overflow-hidden flex-shrink-0 bg-[var(--bg-surface-alt)] flex items-center justify-center border border-[var(--border-default)]">
                          <X size={16} className="text-red-400" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium text-[var(--text-primary)] text-sm">
                          {report.description.length > 100 
                            ? `${report.description.substring(0, 100)}...` 
                            : report.description}
                        </h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="text-sm text-[var(--text-secondary)]">
                            Rapporterad av: {report.reportedBy}
                          </span>
                          <span className="text-sm text-[var(--text-secondary)]">
                            {formatDate(report.reportedAt)}
                          </span>
                          {report.itemId && (
                            <span className="text-sm text-accent-600 font-medium">
                              För specifikt objekt
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <StatusBadge status={report.status} size="md" />
                      {report.photoIds && report.photoIds.length > 0 && (
                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5">
                          <Camera size={14} />
                          {report.photoIds.length} foto{report.photoIds.length !== 1 ? 'n' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DamageReportListPage; 